import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchSalonsForStaffRegistration, registerStaff } from '../api/auth';
import Input from '../components/ui/Input';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useToast } from '../context/ToastContext';
import { useDebounce } from '../hooks/useDebounce';

const RegisterStaffPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  });
  const [selectedSalonIds, setSelectedSalonIds] = useState([]);
  const [salonSearch, setSalonSearch] = useState('');
  const debouncedSalonSearch = useDebounce(salonSearch, 300);
  const [loading, setLoading] = useState(false);
  const [submittedSalons, setSubmittedSalons] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const { data: salonData, isLoading: salonsLoading } = useQuery({
    queryKey: ['register-staff-salons', debouncedSalonSearch],
    queryFn: () => fetchSalonsForStaffRegistration({ q: debouncedSalonSearch || undefined }),
  });

  const salons = useMemo(() => salonData?.salons || [], [salonData]);

  const selectedSalons = useMemo(
    () => salons.filter((salon) => selectedSalonIds.includes(salon._id)),
    [salons, selectedSalonIds]
  );

  const toggleSalon = (salonId) => {
    setSelectedSalonIds((current) =>
      current.includes(salonId) ? current.filter((id) => id !== salonId) : [...current, salonId]
    );
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSalonIds.length) {
      showToast(t('auth.staffRegister.salonRequired'), 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await registerStaff({ ...form, tenantIds: selectedSalonIds });
      setSubmittedSalons(res.salons || selectedSalons.map((s) => ({ tenantId: s._id, businessName: s.businessName })));
      setSubmitted(true);
      showToast(res.message || t('auth.staffRegister.submitted'), 'success');
    } catch (err) {
      showToast(err.response?.data?.message || t('auth.registrationFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-zinc-50 px-4 dark:bg-zinc-950">
        <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-8 text-center dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-4xl">⏳</p>
          <h1 className="mt-4 text-xl font-semibold">{t('auth.staffRegister.pendingTitle')}</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{t('auth.staffRegister.pendingDescMulti')}</p>
          {submittedSalons.length > 0 && (
            <ul className="mt-4 space-y-2 text-left">
              {submittedSalons.map((salon) => (
                <li
                  key={salon.tenantId}
                  className="rounded-lg bg-zinc-100 px-4 py-2 text-sm font-medium dark:bg-zinc-800"
                >
                  {salon.businessName}
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/login"
            className="mt-6 inline-block rounded-lg bg-glow-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-glow-500"
          >
            {t('auth.signInLink')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-50 px-4 py-10 dark:bg-zinc-950">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-8 dark:border-zinc-800 dark:bg-zinc-900">
        <h1 className="text-2xl font-semibold text-glow-400">{t('auth.staffRegister.title')}</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{t('auth.staffRegister.subtitleMulti')}</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label={t('auth.firstName')} id="firstName" value={form.firstName} onChange={update('firstName')} required />
            <Input label={t('auth.lastName')} id="lastName" value={form.lastName} onChange={update('lastName')} required />
          </div>
          <Input label={t('auth.email')} id="email" type="email" value={form.email} onChange={update('email')} required />
          <Input label={t('auth.phone')} id="phone" value={form.phone} onChange={update('phone')} />
          <Input label={t('auth.password')} id="password" type="password" value={form.password} onChange={update('password')} required minLength={8} />

          <div>
            <label htmlFor="salonSearch" className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">
              {t('auth.staffRegister.salonLabelMulti')}
            </label>
            <input
              id="salonSearch"
              value={salonSearch}
              onChange={(e) => setSalonSearch(e.target.value)}
              placeholder={t('auth.staffRegister.salonSearchPlaceholder')}
              className="mb-2 w-full rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-zinc-300 p-2 dark:border-zinc-700">
              {salonsLoading && <p className="px-2 py-1 text-sm text-zinc-500">{t('common.loading')}</p>}
              {!salonsLoading && salons.length === 0 && (
                <p className="px-2 py-1 text-sm text-zinc-500">{t('auth.staffRegister.noSalonsFound')}</p>
              )}
              {salons.map((salon) => {
                const checked = selectedSalonIds.includes(salon._id);
                return (
                  <label
                    key={salon._id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                      checked ? 'bg-glow-600/10' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSalon(salon._id)}
                      className="mt-1"
                    />
                    <span className="text-sm">
                      <span className="font-medium">{salon.businessName}</span>
                      {salon.city ? <span className="text-zinc-500"> · {salon.city}</span> : null}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-zinc-500">
              {selectedSalonIds.length
                ? t('auth.staffRegister.selectedSalonCount', { count: selectedSalonIds.length })
                : t('auth.staffRegister.salonHintMulti')}
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50"
          >
            {loading ? t('auth.creatingAccount') : t('auth.staffRegister.submit')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-600 dark:text-zinc-500">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="text-glow-400 hover:underline">
            {t('auth.signInLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterStaffPage;
